'use client';
import * as React from 'react';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';

import { useActivePage } from '../useActivePage';
import { Navigation, NavigationPageItem } from '../AppProvider';
import {
  NavigationContext,
  DashboardSidebarPageItemContext,
} from '../shared/context';
import {
  isPageItem,
  getItemPath,
  getItemTitle,
  hasSelectedNavigationChildren,
} from '../shared/navigation';

import { MINI_DRAWER_WIDTH } from './shared';
import { getDrawerSxTransitionMixin } from './utils';
import {
  DashboardSidebarPageItem,
  DashboardSidebarPageItemContextProps,
} from './DashboardSidebarPageItem';

interface DashboardSidebarSubNavigationPageItemProps {
  id: string;
  depth: number;
  isMini: boolean;
  isExpanded: boolean;
  onLinkClick: () => void;
  item: NavigationPageItem;
  isFullyExpanded: boolean;
  isFullyCollapsed: boolean;
  sidebarExpandedWidth: number | string;
  onClick: (itemId: string, item: NavigationPageItem) => void;
  renderPageItem?: (
    item: NavigationPageItem,
    params: { mini: boolean }
  ) => React.ReactNode;
}

/**
 * @ignore - internal component.
 */
function DashboardSidebarSubNavigationPageItem({
  id,
  item,
  depth,
  isMini,
  onClick,
  isExpanded,
  onLinkClick,
  renderPageItem,
  isFullyExpanded,
  isFullyCollapsed,
  sidebarExpandedWidth,
}: DashboardSidebarSubNavigationPageItemProps) {
  const navigationContext = React.useContext(NavigationContext);

  const activePage = useActivePage();

  const isActive =
    !!activePage && activePage.path === getItemPath(navigationContext, item);

  // Show as selected in mini sidebar if any of the children matches path, otherwise show as selected if item matches path
  const isSelected =
    activePage && item.children && isMini
      ? hasSelectedNavigationChildren(navigationContext, item, activePage.path)
      : isActive && !item.children;

  const pageItemContextProps: DashboardSidebarPageItemContextProps =
    React.useMemo(
      () => ({
        id,
        isMini,
        onClick,
        expanded: isExpanded,
        selected: isSelected,
        isSidebarFullyExpanded: isFullyExpanded,
        isSidebarFullyCollapsed: isFullyCollapsed,
        renderNestedNavigation: () => (
          <DashboardSidebarSubNavigation
            depth={depth + 1}
            isPopover={isMini}
            onLinkClick={onLinkClick}
            subNavigation={item.children ?? []}
            sidebarExpandedWidth={sidebarExpandedWidth}
          />
        ),
      }),
      [
        depth,
        id,
        isExpanded,
        isFullyCollapsed,
        isFullyExpanded,
        isMini,
        isSelected,
        item.children,
        onClick,
        onLinkClick,
        sidebarExpandedWidth,
      ]
    );

  return (
    <DashboardSidebarPageItemContext.Provider value={pageItemContextProps}>
      {renderPageItem ? (
        renderPageItem(item, { mini: isMini })
      ) : (
        <DashboardSidebarPageItem item={item} />
      )}
    </DashboardSidebarPageItemContext.Provider>
  );
}

interface DashboardSidebarSubNavigationProps {
  depth?: number;
  isMini?: boolean;
  isPopover?: boolean;
  onLinkClick: () => void;
  subNavigation: Navigation;
  isFullyExpanded?: boolean;
  isFullyCollapsed?: boolean;
  hasDrawerTransitions?: boolean;
  sidebarExpandedWidth: number | string;
  renderPageItem?: (
    item: NavigationPageItem,
    params: { mini: boolean }
  ) => React.ReactNode;
}

/**
 * @ignore - internal component.
 */
function DashboardSidebarSubNavigation({
  depth = 0,
  onLinkClick,
  subNavigation,
  isMini = false,
  renderPageItem,
  isPopover = false,
  sidebarExpandedWidth,
  isFullyExpanded = true,
  isFullyCollapsed = false,
  hasDrawerTransitions = false,
}: DashboardSidebarSubNavigationProps) {
  const navigationContext = React.useContext(NavigationContext);

  const activePage = useActivePage();

  const initialExpandedItemIds = React.useMemo(
    () =>
      subNavigation
        .map((navigationItem, navigationItemIndex) => ({
          navigationItem,
          originalIndex: navigationItemIndex,
        }))
        .filter(
          ({ navigationItem }) =>
            isPageItem(navigationItem) &&
            !!activePage &&
            hasSelectedNavigationChildren(
              navigationContext,
              navigationItem,
              activePage.path
            )
        )
        .map(({ originalIndex }) => `page-${depth}-${originalIndex}`),
    [activePage, depth, navigationContext, subNavigation]
  );

  const [expandedItemIds, setExpandedItemIds] = React.useState(
    initialExpandedItemIds
  );

  const handlePageItemClick = React.useCallback(
    (itemId: string, item: NavigationPageItem) => {
      if (item.children && !isMini) {
        setExpandedItemIds((previousValue) =>
          previousValue.includes(itemId)
            ? previousValue.filter(
                (previousValueItemId) => previousValueItemId !== itemId
              )
            : [...previousValue, itemId]
        );
      } else if (!item.children) {
        onLinkClick();
      }
    },
    [isMini, onLinkClick]
  );

  return (
    <List
      sx={{
        padding: 0,
        mt: isPopover && depth === 1 ? 0.5 : 0,
        mb: depth === 0 && !isPopover ? 4 : 0.5,
        width: isMini ? MINI_DRAWER_WIDTH : 'auto',
        minWidth: isPopover && depth === 1 ? 240 : 'auto',
        pl: (isPopover ? 1 : 2) * (isPopover ? depth - 1 : depth),
      }}
    >
      {subNavigation.map((navigationItem, navigationItemIndex) => {
        if (navigationItem.kind === 'header') {
          return (
            <ListSubheader
              key={`subheader-${depth}-${navigationItemIndex}`}
              sx={{
                fontSize: 12,
                fontWeight: '700',
                height: isMini ? 0 : 40,
                ...(hasDrawerTransitions
                  ? getDrawerSxTransitionMixin(isFullyExpanded, 'height')
                  : {}),
                px: 2,
                zIndex: 2,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                minWidth: sidebarExpandedWidth,
              }}
            >
              {getItemTitle(navigationItem)}
            </ListSubheader>
          );
        }

        if (navigationItem.kind === 'divider') {
          const nextItem = subNavigation[navigationItemIndex + 1];

          return (
            <li key={`divider-${depth}-${navigationItemIndex}`}>
              <Divider
                sx={{
                  mx: 1,
                  mt: 1,
                  borderBottomWidth: 2,
                  mb: nextItem?.kind === 'header' && !isMini ? 0 : 1,
                  ...(hasDrawerTransitions
                    ? getDrawerSxTransitionMixin(isFullyExpanded, 'margin')
                    : {}),
                }}
              />
            </li>
          );
        }

        const pageItemId = `page-${depth}-${navigationItemIndex}`;

        return (
          <DashboardSidebarSubNavigationPageItem
            depth={depth}
            id={pageItemId}
            isMini={isMini}
            key={pageItemId}
            item={navigationItem}
            onLinkClick={onLinkClick}
            onClick={handlePageItemClick}
            renderPageItem={renderPageItem}
            isFullyExpanded={isFullyExpanded}
            isFullyCollapsed={isFullyCollapsed}
            sidebarExpandedWidth={sidebarExpandedWidth}
            isExpanded={expandedItemIds.includes(pageItemId)}
          />
        );
      })}
    </List>
  );
}

export { DashboardSidebarSubNavigation };
